/** Lines kept (older ones are dropped). */
const KEEP = 60;
/** A line fades out this long (ms) after it's said, unless the chat is open. */
const FADE_MS = 12_000;

/**
 * Chat, bottom left, above the controls: the last few lines said in this
 * area, each fading after a while. Enter opens a line to type in ("/" opens it
 * with a command started: /help lists them), Enter sends it and Escape
 * doesn't. While it's open the game ignores the keyboard (Input's isTyping).
 * A line from nobody ("") is the server's own, a command's answer.
 */
export class Chat {
  private readonly root = document.getElementById("chat")!;
  private readonly log = this.root.querySelector<HTMLElement>(".chat-log")!;
  private readonly input = this.root.querySelector<HTMLInputElement>(".chat-input")!;

  /** `send` sends a line; while `busy` says so (another box is open), Enter and "/" are left alone. */
  constructor(
    private readonly send: (text: string) => void,
    private readonly busy: () => boolean,
  ) {
    window.addEventListener("keydown", (e) => {
      const el = document.activeElement;
      if (this.isOpen || this.busy() || (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT"))) return;
      if (e.key === "Enter" || e.key === "/") {
        e.preventDefault();
        this.open(e.key === "/" ? "/" : "");
      }
    });
    this.input.addEventListener("keydown", (e) => {
      // Kept to ourselves, so Escape doesn't also close a map or panel behind.
      e.stopPropagation();
      if (e.key === "Enter") {
        e.preventDefault();
        const text = this.input.value.trim();
        this.close();
        if (text) this.send(text);
      } else if (e.key === "Escape") {
        e.preventDefault();
        this.close();
      }
    });
    this.input.addEventListener("blur", () => this.close());
  }

  get isOpen(): boolean {
    return this.root.classList.contains("open");
  }

  /** Open the line to type in, starting with `text`. */
  open(text = ""): void {
    this.root.classList.add("open");
    this.input.value = text;
    this.input.focus();
    this.log.scrollTop = this.log.scrollHeight;
  }

  close(): void {
    if (!this.isOpen) return;
    this.root.classList.remove("open");
    this.input.blur();
  }

  /** A line said by `from` (the server's own if ""). */
  add(from: string, text: string): void {
    const line = document.createElement("div");
    line.className = from ? "chat-line" : "chat-line chat-system";
    if (from) {
      const who = document.createElement("span");
      who.className = "chat-from";
      who.textContent = `${from}: `;
      line.append(who);
    }
    line.append(document.createTextNode(text));
    this.log.append(line);
    while (this.log.children.length > KEEP) this.log.firstElementChild!.remove();
    this.log.scrollTop = this.log.scrollHeight;
    setTimeout(() => line.classList.add("faded"), FADE_MS);
  }
}
